/**
 * La app y las notificaciones del telefono, de este lado.
 *
 * Cuatro cosas que ninguna pantalla hace por su cuenta, y por eso viven en el
 * marco (`Shell` llama a `usePushBridge` una vez):
 *
 *   - TOCAR UNA NOTIFICACION CON LA APP ABIERTA lleva a lo que avisa. El service
 *     worker manda `open-notification` y aqui se navega con el enrutador de la
 *     app, sin recargar: el ticket a medias y la captura sin guardar siguen ahi.
 *   - TOCARLA CON LA APP CERRADA tambien. El service worker abre la app con el
 *     aviso en la direccion (`/?aviso=...`), y aqui se lee y se borra de la
 *     barra en cuanto hay sesion —antes no se puede abrir nada—.
 *   - Al abrir con sesion se le recuerda al servidor de quien es este aparato
 *     (`resyncPush`): el telefono prestado, la suscripcion que el navegador
 *     renovo, la llave que cambio.
 *   - El numero de avisos sin leer va en el icono de la app instalada, donde el
 *     sistema lo permite (Android, Windows, macOS, iPhone con la app en inicio).
 *
 * El destino de cada aviso es el MISMO que el de la campanita
 * (`notification-target.ts`): dos puertas a un aviso, una sola regla.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from './api.js';
import { notificationFeedQuery } from './notification-feed.js';
import { pushTarget } from './notification-target.js';
import { resyncPush } from './push.js';
import { useRouter } from './router.js';
import { requestScreenIntent } from './screen-intent.js';
import { useSession } from './session.js';

/** Lo que manda `public/sw.js` al tocar una notificacion. */
const openMessageSchema = z.object({
  type: z.literal('open-notification'),
  id: z.number().int().nonnegative(),
  kind: z.string(),
  storeId: z.number().int().positive().nullable(),
  productName: z.string().nullable(),
});
type OpenMessage = z.infer<typeof openMessageSchema>;

/** Los parametros con los que el service worker abre la app cerrada. */
export const LAUNCH_PARAMS = {
  id: 'aviso',
  kind: 'tipo',
  storeId: 'tienda',
  productName: 'producto',
} as const;

/** El aviso con el que se abrio la app, si se abrio desde una notificacion. */
export function readLaunchMessage(search: string): OpenMessage | null {
  const params = new URLSearchParams(search);
  const kind = params.get(LAUNCH_PARAMS.kind);
  if (kind === null) return null;
  const parsed = openMessageSchema.safeParse({
    type: 'open-notification',
    id: Number(params.get(LAUNCH_PARAMS.id) ?? 0),
    kind,
    storeId: params.has(LAUNCH_PARAMS.storeId) ? Number(params.get(LAUNCH_PARAMS.storeId)) : null,
    productName: params.get(LAUNCH_PARAMS.productName),
  });
  return parsed.success ? parsed.data : null;
}

export function usePushBridge(): void {
  const { user, storeId, setStoreId } = useSession();
  const { navigate } = useRouter();
  const queryClient = useQueryClient();

  /*
   * Lo que hace tocar un aviso, cualquiera de las dos puertas. Va en una
   * referencia y no en las dependencias de los efectos: cambia en cada render
   * (lleva la sucursal y el enrutador) y los oyentes no tienen por que
   * desmontarse y volverse a poner por eso.
   */
  const openRef = useRef<(message: OpenMessage) => void>(() => undefined);
  openRef.current = (message) => {
    /*
     * El aviso es de UNA sucursal. Si la persona esta parada en otra —y tiene
     * acceso a la del aviso—, se cambia primero: el producto que se acabo en
     * Norte no esta en el inventario de Centro, y abrirlo ahi seria buscarlo
     * donde no existe.
     */
    const target = message.storeId;
    const reachable = target !== null && user?.stores.some((s) => s.storeId === target) === true;
    if (reachable && target !== storeId) setStoreId(target);

    const destination = pushTarget(message.kind, message.productName);
    if (destination) {
      navigate(destination.to);
      if (destination.intent !== undefined) requestScreenIntent(destination.intent);
    }

    // Tocar el aviso en el telefono es leerlo: la burbuja no tiene por que
    // seguirlo contando. La prueba (id 0) no vive en la campanita.
    if (reachable && message.id > 0) {
      void api(`/api/stores/${target}/notifications/read`, { method: 'POST', body: { ids: [message.id] } })
        .then(() => queryClient.invalidateQueries({ queryKey: ['notifications'] }))
        .catch(() => undefined);
    }
  };

  // Los mensajes del service worker.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent): void => {
      const data: unknown = event.data;
      if (typeof data !== 'object' || data === null) return;
      const type = (data as { type?: unknown }).type;
      if (type === 'notification-received') {
        // Llego un aviso con la app abierta: la campanita se pone al dia ya, sin
        // esperar a que el canal en vivo se entere por su lado.
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        return;
      }
      const parsed = openMessageSchema.safeParse(data);
      if (parsed.success) openRef.current(parsed.data);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [queryClient]);

  // La app se abrio desde una notificacion estando cerrada.
  useEffect(() => {
    const message = readLaunchMessage(window.location.search);
    if (!message) return;
    // Se borra de la barra antes de abrir nada: recargar no debe volver a
    // abrir el mismo aviso.
    const url = new URL(window.location.href);
    for (const key of Object.values(LAUNCH_PARAMS)) url.searchParams.delete(key);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    openRef.current(message);
  }, []);

  // De quien es este aparato ahora.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (userId) void resyncPush();
  }, [userId]);

  // El numero en el icono de la app.
  const unread = useQuery({ ...notificationFeedQuery(storeId ?? 0), enabled: storeId !== null }).data?.unreadCount ?? 0;
  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (!nav.setAppBadge) return;
    void (unread > 0 ? nav.setAppBadge(unread) : (nav.clearAppBadge?.() ?? Promise.resolve())).catch(
      () => undefined,
    );
  }, [unread]);
}
